/**
 * Sign PDF multi-placement fields — templates + placement instances.
 */

import {
  applyMarginAlongNearestEdge,
  applyWatermarkContentType,
  DEFAULT_WATERMARK_SETTINGS,
  getWatermarkFontStack,
  resolveSingleMarkPosition,
  rotatedMarkHalfExtents,
  WATERMARK_CONTENT_TYPES,
  WATERMARK_TILE_PRESETS,
} from "../../../watermark/Popup/watermarkSettings";
import {
  createDefaultSignPdfInitialsSettings,
  createDefaultSignPdfSettings,
  defaultSignPdfSizePxForKind,
  getSignPdfPageScope,
  hasSignPdfSignature,
  listSignPdfBakeMarks,
  measureSignPdfText,
  resolveSignPdfFontSizePx,
  resolveSignPdfFontSizeNorm,
  resolveSignPdfLogoDisplaySize,
  signPdfLegacyNormFromPx,
  signPdfStageBasis,
  SIGN_PDF_DEFAULT_SIZE_PX,
  SIGN_PDF_MARK_KINDS,
  SIGN_PDF_SCOPES,
  signPdfSizeScaleFromPx,
  withSignPdfSingleMark,
} from "./signPdfSettings";
import { getSignPdfDomainBlock } from "./signPdfBlockCatalog";

function fontSizeNormForPlacement(placement, fontSizePx, { logo = false } = {}) {
  const norm = Number(placement?.fontSizeNorm);
  const px = Number(fontSizePx);
  if (norm > 0 && Number(placement?.fontSizePx) > 0 && px > 0) {
    return norm * (px / Number(placement.fontSizePx));
  }
  if (norm > 0) return norm;
  if (px > 0) return signPdfLegacyNormFromPx(px);
  return signPdfLegacyNormFromPx(
    defaultSignPdfSizePxForKind(placement?.kind || "text")
  );
}

function fontSizeFieldsForPlacement(placement, fontSizePx, opts = {}) {
  const px = Math.max(1, Number(fontSizePx) || 12);
  return {
    fontSizePx: px,
    fontSizeNorm: fontSizeNormForPlacement(placement, px, opts),
    size: signPdfSizeScaleFromPx(px, opts),
  };
}

export const SIGN_PDF_FIELD_KINDS = {
  SIGNATURE: "signature",
  INITIALS: "initials",
  DATE: "date",
  TEXT: "text",
  CHECKBOX: "checkbox",
};

export const SIGN_PDF_FIELD_OPTIONS = [
  { id: SIGN_PDF_FIELD_KINDS.SIGNATURE, label: "Signature", editable: true },
  { id: SIGN_PDF_FIELD_KINDS.INITIALS, label: "Initials", editable: true },
  { id: SIGN_PDF_FIELD_KINDS.DATE, label: "Date", editable: false },
  { id: SIGN_PDF_FIELD_KINDS.TEXT, label: "Text", editable: false },
  { id: SIGN_PDF_FIELD_KINDS.CHECKBOX, label: "Checkmark", editable: false },
];

/** Default visible label when a new text field is placed. */
export const SIGN_PDF_DEFAULT_TEXT_LABEL = "Text";

export const SIGN_PDF_DATE_FORMATS = [
  { id: "MM_DD_YYYY", label: "MM-DD-YYYY" },
  { id: "DD_MM_YYYY", label: "DD-MM-YYYY" },
  { id: "YYYY_MM_DD", label: "YYYY-MM-DD" },
  { id: "YYYY_DD_MM", label: "YYYY-DD-MM" },
  { id: "MMM_D_YYYY", label: "Month DD, YYYY" },
  { id: "D_MMM_YYYY", label: "DD Month YYYY" },
];

export const SIGN_PDF_TEXT_LABEL_GROUPS = [
  {
    id: "standard",
    heading: "Standard",
    items: [
      { id: "full_name", label: "Full name", text: "Full name" },
      { id: "job_title", label: "Job title", text: "Job title" },
      { id: "company", label: "Company", text: "Company" },
      { id: "email", label: "Email", text: "Email" },
      { id: "phone", label: "Phone", text: "Phone" },
      { id: "address", label: "Address", text: "Address" },
    ],
  },
  {
    id: "signing",
    heading: "Signing",
    items: [
      { id: "printed_name", label: "Printed name", text: "Printed name" },
      { id: "title_role", label: "Title / Role", text: "Title / Role" },
      { id: "witness", label: "Witness", text: "Witness" },
      { id: "notary", label: "Notary", text: "Notary" },
      { id: "approver", label: "Approver", text: "Approver" },
      { id: "authorized_by", label: "Authorized by", text: "Authorized by" },
    ],
  },
  {
    id: "document",
    heading: "Document",
    items: [
      { id: "contract_id", label: "Contract ID", text: "Contract ID" },
      { id: "reference", label: "Reference No.", text: "Reference No." },
      { id: "department", label: "Department", text: "Department" },
      { id: "location", label: "Location", text: "Location" },
      { id: "client", label: "Client", text: "Client" },
      { id: "vendor", label: "Vendor", text: "Vendor" },
    ],
  },
  {
    id: "real_estate",
    heading: "Real Estate",
    items: [
      { id: "re_buyer", label: "Buyer", text: "Buyer" },
      { id: "re_seller", label: "Seller", text: "Seller" },
      { id: "re_landlord", label: "Landlord", text: "Landlord" },
      { id: "re_tenant", label: "Tenant", text: "Tenant" },
      { id: "re_property_address", label: "Property address", text: "Property address" },
      { id: "re_parcel", label: "Parcel / Lot ID", text: "Parcel / Lot ID" },
      { id: "re_agent", label: "Agent / Broker", text: "Agent / Broker" },
      { id: "re_escrow", label: "Escrow officer", text: "Escrow officer" },
      { id: "re_closing_date", label: "Closing date", text: "Closing date" },
      { id: "re_earnest", label: "Earnest money", text: "Earnest money" },
    ],
  },
  {
    id: "legal",
    heading: "Legal",
    items: [
      { id: "leg_plaintiff", label: "Plaintiff", text: "Plaintiff" },
      { id: "leg_defendant", label: "Defendant", text: "Defendant" },
      { id: "leg_counsel", label: "Counsel for", text: "Counsel for" },
      { id: "leg_attorney", label: "Attorney of record", text: "Attorney of record" },
      { id: "leg_case_number", label: "Case number", text: "Case number" },
      { id: "leg_court", label: "Court / Venue", text: "Court / Venue" },
      { id: "leg_exhibit", label: "Exhibit", text: "Exhibit" },
      { id: "leg_affiant", label: "Affiant", text: "Affiant" },
      { id: "leg_deponent", label: "Deponent", text: "Deponent" },
      { id: "leg_governing_law", label: "Governing law", text: "Governing law" },
    ],
  },
  {
    id: "venture_capital",
    heading: "Venture Capital",
    items: [
      { id: "vc_investor", label: "Investor", text: "Investor" },
      { id: "vc_founder", label: "Founder", text: "Founder" },
      { id: "vc_issuer", label: "Company (Issuer)", text: "Company (Issuer)" },
      { id: "vc_lead", label: "Lead investor", text: "Lead investor" },
      { id: "vc_board", label: "Board member", text: "Board member" },
      { id: "vc_cap_table", label: "Cap table ID", text: "Cap table ID" },
      { id: "vc_round", label: "Round / Series", text: "Round / Series" },
      { id: "vc_safe", label: "SAFE / Note ID", text: "SAFE / Note ID" },
      { id: "vc_valuation", label: "Valuation", text: "Valuation" },
      { id: "vc_pro_rata", label: "Pro rata", text: "Pro rata" },
    ],
  },
  {
    id: "hr",
    heading: "HR / Employment",
    items: [
      { id: "hr_employee", label: "Employee", text: "Employee" },
      { id: "hr_employer", label: "Employer", text: "Employer" },
      { id: "hr_manager", label: "Manager", text: "Manager" },
      { id: "hr_position", label: "Position", text: "Position" },
      { id: "hr_start_date", label: "Start date", text: "Start date" },
      { id: "hr_employee_id", label: "Employee ID", text: "Employee ID" },
    ],
  },
  {
    id: "healthcare",
    heading: "Healthcare",
    items: [
      { id: "hc_patient", label: "Patient", text: "Patient" },
      { id: "hc_provider", label: "Provider", text: "Provider" },
      { id: "hc_facility", label: "Facility", text: "Facility" },
      { id: "hc_mrn", label: "MRN", text: "MRN" },
      { id: "hc_consent", label: "Consent type", text: "Consent type" },
    ],
  },
  {
    id: "finance",
    heading: "Finance / Banking",
    items: [
      { id: "fin_borrower", label: "Borrower", text: "Borrower" },
      { id: "fin_lender", label: "Lender", text: "Lender" },
      { id: "fin_account_holder", label: "Account holder", text: "Account holder" },
      { id: "fin_loan_id", label: "Loan ID", text: "Loan ID" },
      { id: "fin_guarantor", label: "Guarantor", text: "Guarantor" },
    ],
  },
  {
    id: "sales",
    heading: "Sales / SaaS",
    items: [
      { id: "sales_customer", label: "Customer", text: "Customer" },
      { id: "sales_account", label: "Account name", text: "Account name" },
      { id: "sales_order_id", label: "Order ID", text: "Order ID" },
      { id: "sales_subscription", label: "Subscription ID", text: "Subscription ID" },
      { id: "sales_bill_to", label: "Bill to", text: "Bill to" },
    ],
  },
  {
    id: "education",
    heading: "Education",
    items: [
      { id: "edu_student", label: "Student", text: "Student" },
      { id: "edu_parent", label: "Parent / Guardian", text: "Parent / Guardian" },
      { id: "edu_teacher", label: "Teacher", text: "Teacher" },
      { id: "edu_administrator", label: "Administrator", text: "Administrator" },
      { id: "edu_school", label: "School / Institution", text: "School / Institution" },
      { id: "edu_student_id", label: "Student ID", text: "Student ID" },
      { id: "edu_program", label: "Program / Course", text: "Program / Course" },
      { id: "edu_grade", label: "Grade / Year", text: "Grade / Year" },
      { id: "edu_enrollment", label: "Enrollment ID", text: "Enrollment ID" },
      { id: "edu_advisor", label: "Advisor", text: "Advisor" },
    ],
  },
];

/** Flat list for lookups (includes default Text label). */
export const SIGN_PDF_TEXT_LABELS = [
  {
    id: "text",
    label: SIGN_PDF_DEFAULT_TEXT_LABEL,
    text: SIGN_PDF_DEFAULT_TEXT_LABEL,
  },
  ...SIGN_PDF_TEXT_LABEL_GROUPS.flatMap((g) => g.items),
];

export function findSignPdfTextLabel(text) {
  const raw = text != null ? String(text) : "";
  return SIGN_PDF_TEXT_LABELS.find((l) => l.text === raw) || null;
}

const MONTHS_FULL = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

function pad2(n) {
  return String(n).padStart(2, "0");
}

export function formatTokenDate(date, formatId) {
  const d = date instanceof Date ? date : new Date(date || Date.now());
  if (Number.isNaN(d.getTime())) return "";
  const yyyy = d.getFullYear();
  const mm = pad2(d.getMonth() + 1);
  const dd = pad2(d.getDate());
  const monthFull = MONTHS_FULL[d.getMonth()];
  switch (formatId) {
    case "MM_DD_YYYY":
      return `${mm}-${dd}-${yyyy}`;
    case "DD_MM_YYYY":
      return `${dd}-${mm}-${yyyy}`;
    case "YYYY_MM_DD":
      return `${yyyy}-${mm}-${dd}`;
    case "YYYY_DD_MM":
      return `${yyyy}-${dd}-${mm}`;
    case "D_MMM_YYYY":
      return `${d.getDate()} ${monthFull} ${yyyy}`;
    case "MMM_D_YYYY":
    default:
      return `${monthFull} ${d.getDate()}, ${yyyy}`;
  }
}

function newPlacementId() {
  return `pl_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

function newBlockId() {
  return `blk_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

/** Drag MIME for signing-block presets (value = preset id). */
export const SIGN_PDF_BLOCK_MIME = "application/x-sign-pdf-signing-block";

/**
 * Signing-block size presets — shared position/margin + relative vertical stack.
 * offsetY is relative to the date (anchor); negative = above (stays on-page at bottom-right).
 */
export const SIGN_PDF_BLOCK_PRESETS = [
  {
    id: "compact",
    label: "Compact",
    description: "Tight stack · small type",
    position: "bottom-right",
    margin: 0.04,
    signatureFontSizePx: 24,
    nameFontSizePx: 10,
    dateFontSizePx: 11,
    offsetY: { name: -0.09, signature: -0.038, date: 0 },
  },
  {
    id: "standard",
    label: "Standard",
    description: "Signature + date · bottom right",
    position: "bottom-right",
    margin: 0.06,
    signatureFontSizePx: 30,
    nameFontSizePx: 12,
    dateFontSizePx: 14,
    offsetY: { name: -0.12, signature: -0.055, date: 0 },
  },
  {
    id: "spacious",
    label: "Spacious",
    description: "Roomy stack · larger type",
    position: "bottom-right",
    margin: 0.08,
    signatureFontSizePx: 36,
    nameFontSizePx: 14,
    dateFontSizePx: 16,
    offsetY: { name: -0.16, signature: -0.075, date: 0 },
  },
];

export function getSignPdfBlockPreset(presetId) {
  return (
    SIGN_PDF_BLOCK_PRESETS.find((p) => p.id === presetId) ||
    SIGN_PDF_BLOCK_PRESETS.find((p) => p.id === "standard") ||
    SIGN_PDF_BLOCK_PRESETS[0]
  );
}

export function listSignPdfPlacementsByBlockId(session, blockId) {
  if (!blockId) return [];
  return listSignPdfPlacements(session).filter((p) => p.blockId === blockId);
}

/** Clear group ids so members can be moved/edited independently. */
export function ungroupSignPdfBlock(session, blockId) {
  if (!blockId) return session;
  const placements = listSignPdfPlacements(session).map((p) =>
    p.blockId === blockId
      ? { ...p, blockId: null, blockPresetId: null }
      : p
  );
  return { ...session, placements };
}

/** Remove every placement that belongs to a signing block. */
export function removeSignPdfBlock(session, blockId) {
  if (!blockId) return session;
  const before = listSignPdfPlacements(session);
  const placements = before.filter((p) => p.blockId !== blockId);
  if (placements.length === before.length) return session;
  const removedActive = before.some(
    (p) => p.blockId === blockId && p.id === session?.activePlacementId
  );
  const active = removedActive
    ? placements[placements.length - 1]?.id || null
    : session?.activePlacementId;
  return { ...session, placements, activePlacementId: active };
}

/**
 * Delete a single field, or the whole signing block when the field is grouped.
 */
export function removeSignPdfPlacementOrBlock(session, placementId) {
  const target = listSignPdfPlacements(session).find((p) => p.id === placementId);
  if (!target) return session;
  if (target.blockId) return removeSignPdfBlock(session, target.blockId);
  return removeSignPdfPlacement(session, placementId);
}

/**
 * Apply the same offset/margin delta to every member of a block (grouped move).
 */
export function patchSignPdfBlockGeometry(
  session,
  blockId,
  memberStarts,
  primaryId,
  primarySynced
) {
  if (!blockId || !Array.isArray(memberStarts) || !memberStarts.length) {
    return session;
  }
  const dOx =
    (Number(primarySynced.offsetX) || 0) -
    (Number(memberStarts.find((m) => m.id === primaryId)?.offsetX) || 0);
  const dOy =
    (Number(primarySynced.offsetY) || 0) -
    (Number(memberStarts.find((m) => m.id === primaryId)?.offsetY) || 0);
  const nextMargin =
    primarySynced.margin !== undefined ? primarySynced.margin : undefined;

  let next = session;
  for (const m of memberStarts) {
    const patch = {
      offsetX: (Number(m.offsetX) || 0) + dOx,
      offsetY: (Number(m.offsetY) || 0) + dOy,
    };
    if (nextMargin !== undefined) patch.margin = nextMargin;
    next = updateSignPdfPlacement(next, m.id, patch);
  }
  return setActiveSignPdfPlacement(next, primaryId);
}

const SIGN_PDF_STAGE_SELECTOR =
  ".sp-preview-canvas .transform-preview__canvas";

function readSignPdfStageSize() {
  if (typeof document === "undefined") {
    return { width: 400, height: 560 };
  }
  const el = document.querySelector(SIGN_PDF_STAGE_SELECTOR);
  return {
    width: el?.offsetWidth || 400,
    height: el?.offsetHeight || 560,
  };
}

function isLogoWatermarkMark(wm) {
  return (
    wm?.contentType === WATERMARK_CONTENT_TYPES.LOGO && Boolean(wm?.logoUrl)
  );
}

/** Center + pixel size for one placement on the preview stage. */
function measureSignPdfPlacementOnStage(session, placement, stageW, stageH) {
  const wm = resolveSignPdfPlacementToWatermark(session, placement);
  if (!wm || !stageW || !stageH) return null;
  const logo = isLogoWatermarkMark(wm);
  const rotation = Number(wm.rotation) || 0;
  const fontFamily = getWatermarkFontStack(wm.fontId || wm.fontFamily);
  const fontSizePx = resolveSignPdfFontSizePx(wm, stageW, stageH);
  let width;
  let height;
  if (logo) {
    const logoSize = resolveSignPdfLogoDisplaySize(
      wm,
      stageW,
      stageH,
      wm.logoNaturalWidth,
      wm.logoNaturalHeight,
      wm.logoCropRect
    );
    width = logoSize.width;
    height = logoSize.height;
  } else {
    const metrics = measureSignPdfText(
      wm.text?.trim() ? wm.text : "Text",
      fontSizePx,
      fontFamily,
      wm.fontWeight,
      {
        letterSpacing: wm.letterSpacing,
        fontStyle: wm.fontStyle,
      }
    );
    width = metrics.width;
    height = metrics.height;
  }
  const base = resolveSingleMarkPosition({
    position: wm.position || "center",
    margin: wm.margin,
    stageWidth: stageW,
    stageHeight: stageH,
    textWidth: width,
    textHeight: height,
    rotationDeg: rotation,
    flushEdges: wm.flushEdges !== false,
  });
  return {
    placement,
    wm,
    width,
    height,
    rotation,
    cx: base.x + (Number(wm.offsetX) || 0),
    cy: base.y + (Number(wm.offsetY) || 0),
  };
}

/** Axis-aligned block bounds in stage pixels + normalized center. */
function measureSignPdfBlockBounds(layouts, stageW, stageH) {
  if (!layouts?.length || !stageW || !stageH) return null;
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const layout of layouts) {
    const { halfW, halfH } = rotatedMarkHalfExtents(
      layout.width,
      layout.height,
      layout.rotation
    );
    const cx = layout.cx * stageW;
    const cy = layout.cy * stageH;
    minX = Math.min(minX, cx - halfW);
    maxX = Math.max(maxX, cx + halfW);
    minY = Math.min(minY, cy - halfH);
    maxY = Math.max(maxY, cy + halfH);
  }
  if (!Number.isFinite(minX)) return null;
  const width = Math.max(4, maxX - minX);
  const height = Math.max(4, maxY - minY);
  return {
    width,
    height,
    cx: (minX + maxX) / 2 / stageW,
    cy: (minY + maxY) / 2 / stageH,
  };
}

/**
 * Position panel → whole block AABB (not the active signature alone).
 * Corner / margin place the dashed block box; X/Y nudge moves it as a unit.
 */
export function applySignPdfBlockPositionPatch(
  session,
  blockId,
  primaryId,
  patch = {}
) {
  if (!blockId || !patch || typeof patch !== "object") return session;
  const members = listSignPdfPlacementsByBlockId(session, blockId);
  if (!members.length) return session;

  const primary =
    members.find((m) => m.id === primaryId) || members[0];
  const positionChanged = Object.prototype.hasOwnProperty.call(
    patch,
    "position"
  );
  const marginChanged = Object.prototype.hasOwnProperty.call(patch, "margin");
  const hasOx = Object.prototype.hasOwnProperty.call(patch, "offsetX");
  const hasOy = Object.prototype.hasOwnProperty.call(patch, "offsetY");

  if (members.length === 1 && !positionChanged && !marginChanged) {
    return updateSignPdfPlacement(session, members[0].id, {
      ...(hasOx ? { offsetX: patch.offsetX } : {}),
      ...(hasOy ? { offsetY: patch.offsetY } : {}),
    });
  }

  const { width: stageW, height: stageH } = readSignPdfStageSize();
  const layouts = members
    .map((m) => measureSignPdfPlacementOnStage(session, m, stageW, stageH))
    .filter(Boolean);
  if (!layouts.length) {
    // Fallback: old relative delta if we can't measure.
    const dOx = hasOx
      ? (Number(patch.offsetX) || 0) - (Number(primary.offsetX) || 0)
      : 0;
    const dOy = hasOy
      ? (Number(patch.offsetY) || 0) - (Number(primary.offsetY) || 0)
      : 0;
    let next = session;
    for (const m of members) {
      const memberPatch = {};
      if (positionChanged) memberPatch.position = patch.position;
      if (marginChanged) memberPatch.margin = patch.margin;
      if (hasOx) memberPatch.offsetX = (Number(m.offsetX) || 0) + dOx;
      if (hasOy) memberPatch.offsetY = (Number(m.offsetY) || 0) + dOy;
      next = updateSignPdfPlacement(next, m.id, memberPatch);
    }
    return setActiveSignPdfPlacement(next, primaryId);
  }

  const bounds = measureSignPdfBlockBounds(layouts, stageW, stageH);
  if (!bounds) return session;

  const nextPosition = positionChanged
    ? patch.position || "center"
    : primary.position || "center";
  const nextMargin = marginChanged
    ? Number(patch.margin)
    : Number(primary.margin) || 0.06;

  let targetCx = bounds.cx;
  let targetCy = bounds.cy;

  if (positionChanged || marginChanged) {
    if (marginChanged && !positionChanged) {
      const applied = applyMarginAlongNearestEdge({
        centerX: bounds.cx,
        centerY: bounds.cy,
        position: nextPosition,
        nextMargin: Math.max(0, Math.min(0.4, nextMargin)),
        stageWidth: stageW,
        stageHeight: stageH,
        textWidth: bounds.width,
        textHeight: bounds.height,
        rotationDeg: 0,
        flushEdges: false,
      });
      const base = resolveSingleMarkPosition({
        position: nextPosition,
        margin: applied.margin,
        stageWidth: stageW,
        stageHeight: stageH,
        textWidth: bounds.width,
        textHeight: bounds.height,
        rotationDeg: 0,
        flushEdges: false,
      });
      targetCx = base.x + (Number(applied.offsetX) || 0);
      targetCy = base.y + (Number(applied.offsetY) || 0);
    } else {
      // Snap the whole block box to the 9-grid corner / edge (margin insets).
      const base = resolveSingleMarkPosition({
        position: nextPosition,
        margin: Number.isFinite(nextMargin) ? nextMargin : 0.06,
        stageWidth: stageW,
        stageHeight: stageH,
        textWidth: bounds.width,
        textHeight: bounds.height,
        rotationDeg: 0,
        flushEdges: false,
      });
      targetCx = base.x;
      targetCy = base.y;
    }
  } else if (hasOx || hasOy) {
    // Pure X/Y nudge: move block by the same delta the panel applied to primary.
    const primaryLayout = layouts.find((l) => l.placement.id === primary.id);
    if (primaryLayout) {
      const primaryBase = resolveSingleMarkPosition({
        position: primary.position || "center",
        margin: primary.margin,
        stageWidth: stageW,
        stageHeight: stageH,
        textWidth: primaryLayout.width,
        textHeight: primaryLayout.height,
        rotationDeg: primaryLayout.rotation,
        flushEdges: primaryLayout.wm.flushEdges !== false,
      });
      const desiredCx = hasOx
        ? primaryBase.x + (Number(patch.offsetX) || 0)
        : primaryLayout.cx;
      const desiredCy = hasOy
        ? primaryBase.y + (Number(patch.offsetY) || 0)
        : primaryLayout.cy;
      targetCx = bounds.cx + (desiredCx - primaryLayout.cx);
      targetCy = bounds.cy + (desiredCy - primaryLayout.cy);
    }
  } else {
    return session;
  }

  const dCx = targetCx - bounds.cx;
  const dCy = targetCy - bounds.cy;
  const sharedMargin = Number.isFinite(nextMargin) ? nextMargin : 0.06;

  let next = session;
  for (const layout of layouts) {
    const newCx = layout.cx + dCx;
    const newCy = layout.cy + dCy;
    const base = resolveSingleMarkPosition({
      position: nextPosition,
      margin: sharedMargin,
      stageWidth: stageW,
      stageHeight: stageH,
      textWidth: layout.width,
      textHeight: layout.height,
      rotationDeg: layout.rotation,
      flushEdges: layout.wm.flushEdges !== false,
    });
    next = updateSignPdfPlacement(next, layout.placement.id, {
      position: nextPosition,
      margin: sharedMargin,
      offsetX: newCx - base.x,
      offsetY: newCy - base.y,
    });
  }
  return setActiveSignPdfPlacement(next, primaryId);
}

/**
 * Snapshot block members for proportional resize (frozen at drag start).
 */
export function snapshotSignPdfBlockForResize(
  session,
  blockId,
  stageW,
  stageH
) {
  const members = listSignPdfPlacementsByBlockId(session, blockId);
  return members
    .map((m) => measureSignPdfPlacementOnStage(session, m, stageW, stageH))
    .filter(Boolean)
    .map((layout) => {
      const fontSizePx = Math.max(
        1,
        resolveSignPdfFontSizePx(layout.wm, stageW, stageH)
      );
      const fontSizeNorm = resolveSignPdfFontSizeNorm(
        { ...layout.placement, ...layout.wm },
        stageW,
        stageH
      );
      return {
        id: layout.placement.id,
        fontSizePx,
        fontSizeNorm,
        isLogo: isLogoWatermarkMark(layout.wm),
        cx: layout.cx,
        cy: layout.cy,
        width: layout.width,
        height: layout.height,
        rotation: layout.rotation,
        position: layout.wm.position || "center",
        margin: layout.wm.margin ?? 0.06,
        flushEdges: layout.wm.flushEdges !== false,
      };
    });
}

/**
 * Uniformly scale a signing block around an anchor (opposite resize handle).
 * Scales type size and member centers so gaps stay proportional.
 */
export function scaleSignPdfBlockFromSnapshot(
  session,
  blockId,
  primaryId,
  snapshot,
  appliedScale,
  anchorX,
  anchorY,
  stageW,
  stageH
) {
  if (
    !blockId ||
    !Array.isArray(snapshot) ||
    !snapshot.length ||
    !Number.isFinite(appliedScale) ||
    appliedScale <= 0
  ) {
    return session;
  }
  let next = session;
  const basis = signPdfStageBasis(stageW, stageH);
  for (const m of snapshot) {
    const startNorm =
      Number(m.fontSizeNorm) > 0
        ? Number(m.fontSizeNorm)
        : Number(m.fontSizePx) / basis;
    const nextNorm = Math.max(
      6 / basis,
      Math.min(240 / basis, startNorm * appliedScale)
    );
    const nextPx = Math.max(6, Math.min(240, nextNorm * basis));
    const newCx = anchorX + (m.cx - anchorX) * appliedScale;
    const newCy = anchorY + (m.cy - anchorY) * appliedScale;
    const newW = Math.max(1, m.width * appliedScale);
    const newH = Math.max(1, m.height * appliedScale);
    const base = resolveSingleMarkPosition({
      position: m.position || "center",
      margin: m.margin,
      stageWidth: stageW,
      stageHeight: stageH,
      textWidth: newW,
      textHeight: newH,
      rotationDeg: m.rotation,
      flushEdges: m.flushEdges !== false,
    });
    next = updateSignPdfPlacement(next, m.id, {
      fontSizePx: Math.round(nextPx * 10) / 10,
      fontSizeNorm: nextNorm,
      size: signPdfSizeScaleFromPx(nextPx, { logo: m.isLogo }),
      offsetX: newCx - base.x,
      offsetY: newCy - base.y,
    });
  }
  return setActiveSignPdfPlacement(next, primaryId || snapshot[0]?.id);
}


/** Empty checkbox — transparent fill, black border only. */
export const SIGN_PDF_CHECKBOX_UNCHECKED_DATA_URL =
  "data:image/svg+xml," +
  encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 64 64">
      <rect x="4" y="4" width="56" height="56" rx="6" fill="none" stroke="#000000" stroke-width="4"/>
    </svg>`
  );

/** Checked checkbox — box + tick. */
export const SIGN_PDF_CHECKBOX_BOXED_DATA_URL =
  "data:image/svg+xml," +
  encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 64 64">
      <rect x="4" y="4" width="56" height="56" rx="6" fill="none" stroke="#000000" stroke-width="4"/>
      <path d="M16 33l10 10 22-24" fill="none" stroke="#000000" stroke-width="6" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>`
  );

/** Just tickmark — no box. */
export const SIGN_PDF_CHECKBOX_DATA_URL =
  "data:image/svg+xml," +
  encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 64 64">
      <path d="M12 34l14 14 26-30" fill="none" stroke="#000000" stroke-width="6" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>`
  );

/** Cross mark — no box. */
export const SIGN_PDF_CHECKBOX_WRONG_DATA_URL =
  "data:image/svg+xml," +
  encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 64 64">
      <path d="M18 18l28 28M46 18L18 46" fill="none" stroke="#000000" stroke-width="6" stroke-linecap="round"/>
    </svg>`
  );

/** empty · box (checked) · check (tick only) · wrong (cross) */
export const SIGN_PDF_CHECKBOX_STYLES = [
  {
    id: "empty",
    label: "Checkbox",
    url: SIGN_PDF_CHECKBOX_UNCHECKED_DATA_URL,
  },
  {
    id: "box",
    label: "Checked checkbox",
    url: SIGN_PDF_CHECKBOX_BOXED_DATA_URL,
  },
  {
    id: "check",
    label: "Just tickmark",
    url: SIGN_PDF_CHECKBOX_DATA_URL,
  },
  {
    id: "wrong",
    label: "Cross mark",
    url: SIGN_PDF_CHECKBOX_WRONG_DATA_URL,
  },
];

const CHECKBOX_STYLE_IDS = new Set(
  SIGN_PDF_CHECKBOX_STYLES.map((s) => s.id)
);

export function resolveSignPdfCheckboxStyle(placement) {
  const raw = placement?.checkboxStyle;
  if (CHECKBOX_STYLE_IDS.has(raw)) return raw;
  // Legacy boolean `checked`
  if (placement?.checked === false) return "empty";
  return "box";
}

export function signPdfCheckboxDataUrl(styleOrChecked = "box") {
  if (styleOrChecked === true) return SIGN_PDF_CHECKBOX_BOXED_DATA_URL;
  if (styleOrChecked === false) return SIGN_PDF_CHECKBOX_UNCHECKED_DATA_URL;
  const style = SIGN_PDF_CHECKBOX_STYLES.find((s) => s.id === styleOrChecked);
  return style?.url || SIGN_PDF_CHECKBOX_BOXED_DATA_URL;
}

function baseTextMark(partial = {}) {
  const fontSizePx =
    Number(partial.fontSizePx) > 0
      ? Number(partial.fontSizePx)
      : SIGN_PDF_DEFAULT_SIZE_PX.text;
  const fontSizeNorm =
    Number(partial.fontSizeNorm) > 0
      ? Number(partial.fontSizeNorm)
      : signPdfLegacyNormFromPx(fontSizePx);
  // Do not call applyWatermarkPosition here — it resets offsetX/Y to 0 and
  // would break drag-move for Date / Text fields.
  return withSignPdfSingleMark({
    ...applyWatermarkContentType(
      {
        ...DEFAULT_WATERMARK_SETTINGS,
        ...WATERMARK_TILE_PRESETS.single,
        text: "",
        size: signPdfSizeScaleFromPx(fontSizePx),
        fontSizePx,
        fontSizeNorm,
        opacity: 100,
        color: "#111111",
        fontId: "Arial",
        fontWeight: "normal",
        fontStyle: "normal",
      },
      WATERMARK_CONTENT_TYPES.TEXT
    ),
    position: partial.position || "center",
    margin: partial.margin ?? 0.06,
    offsetX: Number(partial.offsetX) || 0,
    offsetY: Number(partial.offsetY) || 0,
    ...partial,
    size:
      Number(partial.size) > 0
        ? Number(partial.size)
        : signPdfSizeScaleFromPx(fontSizePx),
    fontSizePx,
    fontSizeNorm,
  });
}

function stackOffset(session, kind, pageNum) {
  const existing = listSignPdfPlacementsForPage(session, pageNum).filter(
    (p) => p.kind === kind
  );
  const n = existing.length;
  return {
    offsetX: Math.min(0.28, 0.04 * n),
    offsetY: Math.min(0.28, 0.04 * n),
  };
}

export function getSignPdfTemplate(session, kind) {
  if (kind === SIGN_PDF_FIELD_KINDS.INITIALS) {
    return session?.initials || createDefaultSignPdfInitialsSettings();
  }
  return session?.signature || createDefaultSignPdfSettings();
}

export function listSignPdfPlacements(session) {
  return Array.isArray(session?.placements) ? session.placements : [];
}

export function listSignPdfPlacementsForPage(session, pageNum = 1) {
  const scope = getSignPdfPageScope(session);
  const all = listSignPdfPlacements(session);
  if (scope === SIGN_PDF_SCOPES.PER_PAGE) {
    return all.filter((p) => Number(p.page) === Number(pageNum));
  }
  // ALL / CURRENT: placements without page, or matching current when CURRENT
  if (scope === SIGN_PDF_SCOPES.CURRENT) {
    return all.filter(
      (p) => p.page == null || Number(p.page) === Number(pageNum)
    );
  }
  return all.filter((p) => p.page == null);
}

export function getActiveSignPdfPlacement(session) {
  const id = session?.activePlacementId;
  if (!id) return null;
  return listSignPdfPlacements(session).find((p) => p.id === id) || null;
}

export function clearPendingTextLabelPick(session) {
  if (!session?.pendingTextLabelPickId) return session;
  return { ...session, pendingTextLabelPickId: null };
}

export function setActiveSignPdfPlacement(session, placementId) {
  return {
    ...session,
    activePlacementId: placementId || null,
    pendingTextLabelPickId: null,
  };
}

/**
 * Merge template + placement geometry into a watermark settings object.
 */
export function resolveSignPdfPlacementToWatermark(session, placement) {
  if (!placement) return null;
  const kind = placement.kind;

  if (
    kind === SIGN_PDF_FIELD_KINDS.SIGNATURE ||
    kind === SIGN_PDF_FIELD_KINDS.INITIALS
  ) {
    const template = getSignPdfTemplate(session, kind);
    if (!hasSignPdfSignature(template)) return null;
    const fontSizePx =
      Number(placement.fontSizePx) ||
      Number(template.fontSizePx) ||
      defaultSignPdfSizePxForKind(kind);
    const fontSizeNorm =
      Number(placement.fontSizeNorm) > 0
        ? Number(placement.fontSizeNorm)
        : Number(template.fontSizeNorm) > 0
          ? Number(template.fontSizeNorm)
          : signPdfLegacyNormFromPx(fontSizePx);
    return withSignPdfSingleMark({
      ...template,
      offsetX: Number(placement.offsetX) || 0,
      offsetY: Number(placement.offsetY) || 0,
      size:
        Number(placement.size) ||
        template.size ||
        signPdfSizeScaleFromPx(defaultSignPdfSizePxForKind(kind)),
      fontSizePx,
      fontSizeNorm,
      rotation: Number(placement.rotation) || 0,
      position: placement.position || template.position || "center",
      margin: placement.margin ?? template.margin ?? 0.06,
      enabled: true,
      tile: "single",
    });
  }

  if (kind === SIGN_PDF_FIELD_KINDS.DATE) {
    const formatId = placement.dateFormat || "MMM_D_YYYY";
    const raw = placement.dateValue
      ? new Date(placement.dateValue)
      : new Date();
    const text = formatTokenDate(raw, formatId);
    const fontSizePx =
      Number(placement.fontSizePx) || SIGN_PDF_DEFAULT_SIZE_PX.date;
    return baseTextMark({
      text,
      size: Number(placement.size) || signPdfSizeScaleFromPx(fontSizePx),
      fontSizePx,
      fontSizeNorm: fontSizeNormForPlacement(placement, fontSizePx),
      offsetX: Number(placement.offsetX) || 0,
      offsetY: Number(placement.offsetY) || 0,
      rotation: Number(placement.rotation) || 0,
      position: placement.position || "center",
      margin: placement.margin ?? 0.06,
      color: placement.color || "#111111",
      opacity:
        Number.isFinite(Number(placement.opacity))
          ? Number(placement.opacity)
          : 100,
      fontId: placement.fontId || "Arial",
      fontWeight: placement.fontWeight || "normal",
      fontStyle: placement.fontStyle || "normal",
    });
  }

  if (kind === SIGN_PDF_FIELD_KINDS.TEXT) {
    const raw = placement.text != null ? String(placement.text) : "";
    const fontSizePx =
      Number(placement.fontSizePx) || SIGN_PDF_DEFAULT_SIZE_PX.text;
    return baseTextMark({
      text: raw,
      size: Number(placement.size) || signPdfSizeScaleFromPx(fontSizePx),
      fontSizePx,
      fontSizeNorm: fontSizeNormForPlacement(placement, fontSizePx),
      offsetX: Number(placement.offsetX) || 0,
      offsetY: Number(placement.offsetY) || 0,
      rotation: Number(placement.rotation) || 0,
      position: placement.position || "center",
      margin: placement.margin ?? 0.06,
      color: placement.color || "#111111",
      opacity:
        Number.isFinite(Number(placement.opacity))
          ? Number(placement.opacity)
          : 100,
      fontId: placement.fontId || "Arial",
      fontWeight: placement.fontWeight || "normal",
      fontStyle: placement.fontStyle || "normal",
    });
  }

  if (kind === SIGN_PDF_FIELD_KINDS.CHECKBOX) {
    const fontSizePx =
      Number(placement.fontSizePx) || SIGN_PDF_DEFAULT_SIZE_PX.checkbox;
    const boxUrl = signPdfCheckboxDataUrl(
      resolveSignPdfCheckboxStyle(placement)
    );
    return withSignPdfSingleMark({
      ...applyWatermarkContentType(
        {
          ...DEFAULT_WATERMARK_SETTINGS,
          ...WATERMARK_TILE_PRESETS.single,
        },
        WATERMARK_CONTENT_TYPES.LOGO
      ),
      logoUrl: boxUrl,
      logoOriginalUrl: boxUrl,
      logoNaturalWidth: 64,
      logoNaturalHeight: 64,
      size:
        Number(placement.size) ||
        signPdfSizeScaleFromPx(fontSizePx, { logo: true }),
      fontSizePx,
      fontSizeNorm: fontSizeNormForPlacement(placement, fontSizePx, {
        logo: true,
      }),
      offsetX: Number(placement.offsetX) || 0,
      offsetY: Number(placement.offsetY) || 0,
      rotation: Number(placement.rotation) || 0,
      position: placement.position || "center",
      margin: placement.margin ?? 0.06,
      color: placement.color || "#9E9E9E",
      logoTintColor: placement.color || "#9E9E9E",
      opacity:
        Number.isFinite(Number(placement.opacity))
          ? Number(placement.opacity)
          : 100,
      enabled: true,
      text: "",
    });
  }

  return null;
}

export function resolveSignPdfPlacementsForPage(session, pageNum = 1) {
  return listSignPdfPlacementsForPage(session, pageNum)
    .map((p) => resolveSignPdfPlacementToWatermark(session, p))
    .filter(Boolean);
}

/**
 * Bake/thumbnail resolver — placements first, else legacy signature+initials.
 */
export function resolveSignPdfMarksForPage(session, pageNum, currentPage = 1) {
  const placements = listSignPdfPlacements(session);
  if (placements.length) {
    const scope = getSignPdfPageScope(session);
    if (
      scope === SIGN_PDF_SCOPES.CURRENT &&
      Number(pageNum) !== Number(currentPage)
    ) {
      return [];
    }
    return resolveSignPdfPlacementsForPage(session, pageNum);
  }

  const scope = getSignPdfPageScope(session);
  if (scope === SIGN_PDF_SCOPES.PER_PAGE) {
    const key = String(pageNum);
    const entry = session?.byPage?.[key];
    if (!entry) return [];
    const marks = [];
    if (hasSignPdfSignature(entry.signature)) {
      marks.push(
        withSignPdfSingleMark({ ...entry.signature, enabled: true })
      );
    }
    if (hasSignPdfSignature(entry.initials)) {
      marks.push(
        withSignPdfSingleMark({ ...entry.initials, enabled: true })
      );
    }
    return marks;
  }

  const marks = listSignPdfBakeMarks(session);
  if (!marks.length) return [];
  if (scope === SIGN_PDF_SCOPES.CURRENT) {
    return Number(pageNum) === Number(currentPage) ? marks : [];
  }
  return marks;
}

export function listAllResolvedSignPdfMarks(session) {
  const placements = listSignPdfPlacements(session);
  if (placements.length) {
    return placements
      .map((p) => resolveSignPdfPlacementToWatermark(session, p))
      .filter(Boolean);
  }
  return listSignPdfBakeMarks(session);
}

function defaultGeometryForKind(kind) {
  // New stamps land in the page center; user can drag afterward.
  const fontSizePx = defaultSignPdfSizePxForKind(kind);
  const fontSizeNorm = signPdfLegacyNormFromPx(fontSizePx);
  const size = signPdfSizeScaleFromPx(fontSizePx, {
    logo: kind === SIGN_PDF_FIELD_KINDS.CHECKBOX,
  });
  if (kind === SIGN_PDF_FIELD_KINDS.SIGNATURE) {
    return { position: "center", size, fontSizePx, fontSizeNorm, margin: 0.06 };
  }
  if (kind === SIGN_PDF_FIELD_KINDS.INITIALS) {
    return { position: "center", size, fontSizePx, fontSizeNorm, margin: 0.06 };
  }
  if (kind === SIGN_PDF_FIELD_KINDS.DATE) {
    return { position: "center", size, fontSizePx, fontSizeNorm, margin: 0.08 };
  }
  if (kind === SIGN_PDF_FIELD_KINDS.CHECKBOX) {
    return { position: "center", size, fontSizePx, fontSizeNorm, margin: 0.06 };
  }
  return { position: "center", size, fontSizePx, fontSizeNorm, margin: 0.06 };
}

/**
 * Create a new placement instance and select it.
 */
export function addSignPdfPlacement(session, kind, pageNum = 1, patch = {}) {
  const scope = getSignPdfPageScope(session);
  const stack = stackOffset(session, kind, pageNum);
  const geo = defaultGeometryForKind(kind);
  const placement = {
    id: newPlacementId(),
    kind,
    page: scope === SIGN_PDF_SCOPES.PER_PAGE ? Number(pageNum) : null,
    offsetX: stack.offsetX,
    offsetY: stack.offsetY,
    size: geo.size,
    fontSizePx: geo.fontSizePx,
    fontSizeNorm: geo.fontSizeNorm,
    rotation: 0,
    position: geo.position,
    margin: geo.margin,
    ...patch,
  };

  if (kind === SIGN_PDF_FIELD_KINDS.DATE) {
    placement.dateValue = patch.dateValue ?? null;
    placement.dateFormat = patch.dateFormat || "MMM_D_YYYY";
    placement.color = patch.color || "#111111";
    placement.fontId = patch.fontId || "Arial";
    placement.fontWeight = patch.fontWeight || "normal";
    placement.fontStyle = patch.fontStyle || "normal";
  }
  if (kind === SIGN_PDF_FIELD_KINDS.TEXT) {
    placement.text =
      patch.text != null ? patch.text : SIGN_PDF_DEFAULT_TEXT_LABEL;
    placement.color = patch.color || "#111111";
    placement.fontId = patch.fontId || "Arial";
    placement.fontWeight = patch.fontWeight || "normal";
    placement.fontStyle = patch.fontStyle || "normal";
  }
  if (kind === SIGN_PDF_FIELD_KINDS.CHECKBOX) {
    placement.checkboxStyle = patch.checkboxStyle || "box";
    placement.checked = placement.checkboxStyle !== "empty";
    placement.color = patch.color || "#9E9E9E";
  }

  return {
    ...session,
    placements: [...listSignPdfPlacements(session), placement],
    activePlacementId: placement.id,
    pendingTextLabelPickId:
      kind === SIGN_PDF_FIELD_KINDS.TEXT ? placement.id : null,
  };
}

export function updateSignPdfPlacement(session, placementId, patch) {
  if (!placementId) return session;
  const placements = listSignPdfPlacements(session).map((p) =>
    p.id === placementId ? { ...p, ...patch } : p
  );
  return { ...session, placements };
}

export function removeSignPdfPlacement(session, placementId) {
  const removed = listSignPdfPlacements(session).find((p) => p.id === placementId);
  let placements = listSignPdfPlacements(session).filter(
    (p) => p.id !== placementId
  );
  const blockId = removed?.blockId;
  if (blockId) {
    const remaining = placements.filter((p) => p.blockId === blockId);
    if (remaining.length < 2) {
      placements = placements.map((p) =>
        p.blockId === blockId
          ? { ...p, blockId: null, blockPresetId: null }
          : p
      );
    }
  }
  const active =
    session?.activePlacementId === placementId
      ? placements[placements.length - 1]?.id || null
      : session?.activePlacementId;
  return { ...session, placements, activePlacementId: active };
}

/** Clone active placement with a slight offset (leaves the original block). */
export function duplicateSignPdfPlacement(session, placementId, pageNum = 1) {
  const src = listSignPdfPlacements(session).find((p) => p.id === placementId);
  if (!src) return session;
  const stack = stackOffset(session, src.kind, pageNum);
  const copy = {
    ...src,
    id: newPlacementId(),
    blockId: null,
    blockPresetId: null,
    offsetX: (Number(src.offsetX) || 0) + 0.045 + stack.offsetX * 0.2,
    offsetY: (Number(src.offsetY) || 0) + 0.045 + stack.offsetY * 0.2,
  };
  return {
    ...session,
    placements: [...listSignPdfPlacements(session), copy],
    activePlacementId: copy.id,
  };
}

/**
 * Patch the active placement from watermark toolbar/preview changes.
 * Geometry updates the placement; signature/initials style updates the shared template.
 */
export function patchActiveSignPdfPlacement(session, partial, pageNum = 1) {
  const active = getActiveSignPdfPlacement(session);
  if (!active) return session;

  const geoKeys = [
    "offsetX",
    "offsetY",
    "size",
    "fontSizePx",
    "fontSizeNorm",
    "fontSizeRefBasis",
    "rotation",
    "position",
    "margin",
    "opacity",
    "color",
    "fontId",
    "fontWeight",
    "fontStyle",
    "underline",
    "strikethrough",
    "textAlign",
    "letterSpacing",
    "lineHeight",
    "effect",
  ];

  if (
    active.kind === SIGN_PDF_FIELD_KINDS.SIGNATURE ||
    active.kind === SIGN_PDF_FIELD_KINDS.INITIALS
  ) {
    const geoPatch = {};
    for (const key of [
      "offsetX",
      "offsetY",
      "size",
      "fontSizePx",
      "fontSizeNorm",
      "fontSizeRefBasis",
      "rotation",
      "position",
      "margin",
    ]) {
      if (Object.prototype.hasOwnProperty.call(partial, key)) {
        geoPatch[key] = partial[key];
      }
    }
    let next = updateSignPdfPlacement(session, active.id, geoPatch);

    const templatePatch = { ...partial };
    for (const key of Object.keys(geoPatch)) delete templatePatch[key];
    if (Object.keys(templatePatch).length) {
      const template = getSignPdfTemplate(next, active.kind);
      const merged = withSignPdfSingleMark({ ...template, ...templatePatch });
      if (active.kind === SIGN_PDF_FIELD_KINDS.INITIALS) {
        next = { ...next, initials: merged };
      } else {
        next = { ...next, signature: merged };
      }
    }
    return next;
  }

  const placementPatch = {};
  for (const key of geoKeys) {
    if (Object.prototype.hasOwnProperty.call(partial, key)) {
      placementPatch[key] = partial[key];
    }
  }
  if (Object.prototype.hasOwnProperty.call(partial, "text")) {
    placementPatch.text = partial.text;
  }
  if (Object.prototype.hasOwnProperty.call(partial, "checkboxStyle")) {
    const style = resolveSignPdfCheckboxStyle({
      checkboxStyle: partial.checkboxStyle,
    });
    placementPatch.checkboxStyle = style;
    placementPatch.checked = style !== "empty";
  } else if (Object.prototype.hasOwnProperty.call(partial, "checked")) {
    placementPatch.checked = Boolean(partial.checked);
    placementPatch.checkboxStyle = partial.checked ? "box" : "empty";
  }
  if (Object.prototype.hasOwnProperty.call(partial, "dateFormat")) {
    placementPatch.dateFormat = partial.dateFormat;
  }
  if (Object.prototype.hasOwnProperty.call(partial, "dateValue")) {
    placementPatch.dateValue = partial.dateValue;
  }
  return updateSignPdfPlacement(session, active.id, placementPatch);
}

/**
 * After saving signature/initials template from modal — update template
 * and ensure at least one placement exists on the page.
 */
export function applySignPdfTemplateAndPlace(
  session,
  kind,
  markSettings,
  pageNum = 1
) {
  const fieldKind =
    kind === SIGN_PDF_MARK_KINDS.INITIALS ||
    kind === SIGN_PDF_FIELD_KINDS.INITIALS
      ? SIGN_PDF_FIELD_KINDS.INITIALS
      : SIGN_PDF_FIELD_KINDS.SIGNATURE;

  const stamped = withSignPdfSingleMark({
    ...(markSettings || {}),
    tile: "single",
    enabled: true,
  });

  let next =
    fieldKind === SIGN_PDF_FIELD_KINDS.INITIALS
      ? { ...session, initials: stamped }
      : { ...session, signature: stamped };

  const pagePlacements = listSignPdfPlacementsForPage(next, pageNum).filter(
    (p) => p.kind === fieldKind
  );
  if (!pagePlacements.length) {
    next = addSignPdfPlacement(next, fieldKind, pageNum);
  } else {
    next = setActiveSignPdfPlacement(next, pagePlacements[0].id);
  }
  return next;
}

export function getActiveResolvedSignPdfMark(session, pageNum = 1) {
  const active = getActiveSignPdfPlacement(session);
  if (!active) return null;
  const onPage = listSignPdfPlacementsForPage(session, pageNum).some(
    (p) => p.id === active.id
  );
  if (!onPage) return null;
  return resolveSignPdfPlacementToWatermark(session, active);
}

export function getExtraSignPdfWatermarks(session, pageNum = 1) {
  const activeId = session?.activePlacementId;
  return listSignPdfPlacementsForPage(session, pageNum)
    .filter((p) => p.id !== activeId)
    .map((p) => {
      const wm = resolveSignPdfPlacementToWatermark(session, p);
      if (!wm) return null;
      return { ...wm, signPdfPlacementId: p.id };
    })
    .filter(Boolean);
}

export function hasAnySignPdfPlacement(session) {
  return listSignPdfPlacements(session).some((p) => {
    if (
      p.kind === SIGN_PDF_FIELD_KINDS.SIGNATURE ||
      p.kind === SIGN_PDF_FIELD_KINDS.INITIALS
    ) {
      return hasSignPdfSignature(getSignPdfTemplate(session, p.kind));
    }
    if (p.kind === SIGN_PDF_FIELD_KINDS.TEXT) {
      return true; // blank text still a stamp
    }
    return true;
  });
}

export function canPlaceSignPdfField(session, kind) {
  if (
    kind === SIGN_PDF_FIELD_KINDS.SIGNATURE ||
    kind === SIGN_PDF_FIELD_KINDS.INITIALS
  ) {
    return hasSignPdfSignature(getSignPdfTemplate(session, kind));
  }
  return true;
}

/**
 * Place a signing-block preset: optional typed name + signature + date.
 * Members share blockId (grouped move until ungrouped).
 *
 * @param {object} [options]
 * @param {string} [options.presetId] size: compact | standard | spacious
 * @param {string} [options.domainId] catalog block id (classic, contract, …)
 */
export function addSignPdfSigningBlock(
  session,
  pageNum = 1,
  { presetId = "standard", domainId = null } = {}
) {
  if (!canPlaceSignPdfField(session, SIGN_PDF_FIELD_KINDS.SIGNATURE)) {
    return session;
  }

  let domain = domainId ? getSignPdfDomainBlock(domainId) : null;

  const sizeId = presetId || domain?.defaultSizeId || "standard";
  const preset = getSignPdfBlockPreset(sizeId);
  const template = getSignPdfTemplate(session, SIGN_PDF_FIELD_KINDS.SIGNATURE);
  const typedName = String(template?.text || "").trim();
  const includeName = domain ? domain.includeName !== false : true;
  const includeDate = domain ? domain.includeDate !== false : true;
  const name = includeName ? typedName : "";
  const blockId = newBlockId();
  const block = {
    position: domain?.position || preset.position || "bottom-right",
    margin: preset.margin ?? 0.06,
    offsetX: 0,
    blockId,
    blockPresetId: preset.id,
    blockDomainId: domain?.id || null,
  };

  let next = session;
  let signatureId = null;

  if (name) {
    next = addSignPdfPlacement(next, SIGN_PDF_FIELD_KINDS.TEXT, pageNum, {
      ...block,
      offsetY: preset.offsetY?.name ?? -0.12,
      text: name,
      ...fontSizeFieldsForPlacement(null, preset.nameFontSizePx ?? 12),
      color: template.color || "#111111",
      fontId: "Arial",
    });
  }

  next = addSignPdfPlacement(next, SIGN_PDF_FIELD_KINDS.SIGNATURE, pageNum, {
    ...block,
    offsetY: name
      ? preset.offsetY?.signature ?? -0.055
      : (preset.offsetY?.signature ?? -0.04) + 0.015,
    ...fontSizeFieldsForPlacement(null, preset.signatureFontSizePx ?? 30),
  });
  signatureId = getActiveSignPdfPlacement(next)?.id || null;

  if (includeDate) {
    next = addSignPdfPlacement(next, SIGN_PDF_FIELD_KINDS.DATE, pageNum, {
      ...block,
      offsetY: preset.offsetY?.date ?? 0,
      ...fontSizeFieldsForPlacement(null, preset.dateFontSizePx ?? 14),
      color: "#111111",
    });
  }

  if (signatureId) {
    next = setActiveSignPdfPlacement(next, signatureId);
  }
  return next;
}

/** empty | partial (fields, no sig) | signed (has signature or initials) */
export function getSignPdfPageSigningStatus(session, pageNum = 1) {
  const list = listSignPdfPlacementsForPage(session, pageNum);
  if (!list.length) return "empty";
  const hasMark = list.some(
    (p) =>
      p.kind === SIGN_PDF_FIELD_KINDS.SIGNATURE ||
      p.kind === SIGN_PDF_FIELD_KINDS.INITIALS
  );
  return hasMark ? "signed" : "partial";
}
